import React, { useState } from "react";
import styled from "styled-components";
import { Document, Page, pdfjs } from "react-pdf";
import { useTranslation } from "react-i18next";

//custom components
import Loader from "../Loader/Loader.component";

//styles
import { bgSections, linkMenuHoverBg, infoColor, bgHeaders } from "../../../styles/theme";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const ControlsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  position: sticky;
  top: 0;
  left: 0;
  z-index: 2;
  background: ${bgHeaders};

  .zoom-controls,
  .pages-wrapper {
    display: flex;
    padding: calc(4 * var(--atom));
  }

  .btn-control {
    width: calc(6 * var(--atom));
    height: calc(6 * var(--atom));
    border: none;
    background-color: ${linkMenuHoverBg};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${infoColor};
    &.disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
  }
  span {
    width: calc(10 * var(--atom));
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: calc(3 * var(--atom));
    line-height: calc(3 * var(--atom));
  }
`;

const DocumentWrapper = styled.div`
  width: 100%;
  position: relative;
  background: ${bgSections};
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  overflow-x: auto;
  height: calc(90vh - calc(22.5 * var(--atom)));

  canvas {
    margin: 0 auto;
  }

  .controls-wrapper {
    display: flex;
    justify-content: space-between;
    position: sticky;
    top: 0;
    left: 0;
  }
`;

const ErrorWrapper = styled.div`
  padding: calc(2 * var(--atom)) 0;
`;

interface Props {
  file: any;
}

const iconPlus = (
  <svg
    width="16"
    height="2"
    viewBox="0 0 16 2"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1 1.29353L15 1.22607"
      stroke="#212121"
      strokeWidth="0.992"
      strokeLinecap="round"
    />
  </svg>
);

const iconMinus = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1 8.29353L15 8.22607"
      stroke="#212121"
      strokeWidth="0.992"
      strokeLinecap="round"
    />
    <path
      d="M7.83996 15L7.77441 1"
      stroke="#212121"
      strokeWidth="0.992"
      strokeLinecap="round"
    />
  </svg>
);

const arrowLeft = (
  <svg
    width="7"
    height="13"
    viewBox="0 0 7 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.95545 12L1 6.51148L6 1"
      stroke="#212121"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const arrowRight = (
  <svg
    width="7"
    height="13"
    viewBox="0 0 7 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.04455 12L6 6.51148L1 1"
      stroke="#212121"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PdfViewer: React.FC<Props> = ({ file }) => {
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [numPages, setNumPages] = useState<number>(1);
  const [pdfError, setPdfError] = useState<boolean>(true);

  const [zoomPercent, setZoomPercent] = useState<number>(125);

  const { t } = useTranslation();

  const onDocumentLoadSuccess = ({ numPages }: any) => {
    setNumPages(numPages);
    setPageNumber(1);
    setZoomPercent(125);
    setPdfError(false);
  };

  return (
    <>
      {!pdfError && (
        <ControlsWrapper>
          <div className="zoom-controls">
            <div
              className={`btn-control${zoomPercent === 50 ? " disabled" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                if (zoomPercent > 50) {
                  setZoomPercent(zoomPercent - 25);
                }
              }}
            >
              {iconPlus}
            </div>
            <span className="percent">{zoomPercent}%</span>
            <div
              className={`btn-control${zoomPercent === 250 ? " disabled" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                if (zoomPercent < 250) {
                  setZoomPercent(zoomPercent + 25);
                }
              }}
            >
              {iconMinus}
            </div>
          </div>
          <div className="pages-wrapper">
            <div
              className={`btn-control${pageNumber === 1 ? " disabled" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                if (pageNumber > 1) {
                  setPageNumber(pageNumber - 1);
                }
              }}
            >
              {arrowLeft}
            </div>
            <span className="percent">{`${pageNumber} / ${numPages}`}</span>
            <div
              className={`btn-control${pageNumber === numPages ? " disabled" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                if (pageNumber < numPages) {
                  setPageNumber(pageNumber + 1);
                }
              }}
            >
              {arrowRight}
            </div>
          </div>
        </ControlsWrapper>
      )}
      <DocumentWrapper>
        <Document
          file={`${file}-download`}
          onLoadSuccess={onDocumentLoadSuccess}
          error={<ErrorWrapper>{t("common.errorPdfLoading")}</ErrorWrapper>}
          onLoadError={() => setPdfError(true)}
          loading={
            <Loader
              // relative
              showExplicit
            />
          }
        >
          <Page
            pageNumber={pageNumber}
            scale={zoomPercent / 100}
          />
        </Document>
      </DocumentWrapper>
    </>
  );
};

export default PdfViewer;
